import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { MaskedLines, FadeUp } from "@/components/Reveal";
import { API, whatsappLink, IMAGES } from "@/config";

const PROPERTY_TYPES = [
    "Un solo immobile",
    "2 immobili",
    "Portfolio (3+ immobili)",
    "Investitore / altro",
];

const initial = { name: "", email: "", phone: "", property_type: "", message: "" };

export default function Contact() {
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Inserisci almeno nome ed email.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/leads`, {
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim() || null,
                property_type: form.property_type || null,
                message: form.message.trim() || null,
            });
            setSent(true);
            setForm(initial);
            toast.success("Richiesta inviata! Ti ricontattiamo a breve.");
        } catch (err) {
            toast.error("Qualcosa è andato storto. Riprova o scrivici su WhatsApp.");
        } finally {
            setLoading(false);
        }
    };

    const inputCls =
        "w-full border-0 border-b border-[var(--fama-border)] bg-transparent py-3 font-body text-base text-[var(--fama-ink)] placeholder:text-[var(--fama-ink-soft)]/60 focus:border-[var(--fama-gold)] focus:outline-none focus:ring-0 transition-colors";

    return (
        <section
            id="contatti"
            className="relative scroll-mt-24 overflow-hidden bg-[var(--fama-ink)] py-24 lg:py-32"
            data-testid="contact-section"
        >
            <div className="pointer-events-none absolute inset-0 opacity-10">
                <img
                    src={IMAGES.ctaBackground}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                />
            </div>

            <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-16 px-5 sm:px-8 lg:grid-cols-2 lg:gap-24">
                {/* Left: CTA copy */}
                <div>
                    <FadeUp>
                        <span className="font-body text-xs font-semibold uppercase tracking-[0.28em] text-[var(--fama-gold)]">
                            Parliamone insieme
                        </span>
                    </FadeUp>
                    <MaskedLines
                        as="h2"
                        lines={["Il primo passo", "è una chiacchierata,", "senza impegno."]}
                        className="mt-5 font-serif-display text-4xl leading-[1.02] tracking-[-0.01em] text-[#fdfbf7] sm:text-5xl lg:text-6xl"
                    />
                    <FadeUp delay={0.1}>
                        <p className="mt-7 max-w-lg font-body text-base leading-relaxed text-[#fdfbf7]/75 sm:text-lg">
                            Raccontaci del tuo immobile: valutiamo insieme il
                            potenziale e ti spieghiamo come possiamo gestirlo al
                            meglio. Nessun vincolo, solo risposte concrete.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.18}>
                        <a
                            href={whatsappLink()}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="contact-whatsapp-button"
                            className="mt-9 inline-flex items-center gap-3 rounded-full bg-[var(--wa-green)] px-8 py-4 font-body text-base font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[var(--wa-green-hover)]"
                        >
                            <WhatsAppIcon size={20} /> Scrivici su WhatsApp
                        </a>
                    </FadeUp>
                </div>

                {/* Right: functional form */}
                <FadeUp delay={0.12}>
                    <div className="rounded-[2px] border border-[var(--fama-border)] bg-[var(--fama-bg)] p-7 sm:p-9">
                        {sent ? (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.96 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="flex min-h-[420px] flex-col items-center justify-center text-center"
                                data-testid="contact-success"
                            >
                                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--fama-gold)] text-white">
                                    <Check size={30} />
                                </span>
                                <h3 className="mt-6 font-serif-display text-3xl text-[var(--fama-ink)]">
                                    Grazie!
                                </h3>
                                <p className="mt-3 max-w-sm font-body text-sm text-[var(--fama-ink-soft)]">
                                    Abbiamo ricevuto la tua richiesta. Ti
                                    ricontatteremo al più presto.
                                </p>
                                <button
                                    onClick={() => setSent(false)}
                                    data-testid="contact-send-another"
                                    className="mt-6 font-body text-sm font-semibold text-[var(--fama-gold)] underline underline-offset-4"
                                >
                                    Invia un'altra richiesta
                                </button>
                            </motion.div>
                        ) : (
                            <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
                                <div>
                                    <label className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-[var(--fama-ink-soft)]">
                                        Nome e cognome *
                                    </label>
                                    <input
                                        type="text"
                                        value={form.name}
                                        onChange={(e) => update("name", e.target.value)}
                                        placeholder="Il tuo nome"
                                        className={inputCls}
                                        data-testid="contact-input-name"
                                        required
                                    />
                                </div>
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <div>
                                        <label className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-[var(--fama-ink-soft)]">
                                            Email *
                                        </label>
                                        <input
                                            type="email"
                                            value={form.email}
                                            onChange={(e) => update("email", e.target.value)}
                                            placeholder="nome@email.it"
                                            className={inputCls}
                                            data-testid="contact-input-email"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-[var(--fama-ink-soft)]">
                                            Telefono
                                        </label>
                                        <input
                                            type="tel"
                                            value={form.phone}
                                            onChange={(e) => update("phone", e.target.value)}
                                            placeholder="+39 ..."
                                            className={inputCls}
                                            data-testid="contact-input-phone"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="mb-1 block font-body text-xs font-semibold uppercase tracking-[0.18em] text-[var(--fama-ink-soft)]">
                                        Tipo di immobile
                                    </label>
                                    <Select
                                        value={form.property_type}
                                        onValueChange={(v) => update("property_type", v)}
                                    >
                                        <SelectTrigger
                                            data-testid="contact-select-property"
                                            className="w-full rounded-none border-0 border-b border-[var(--fama-border)] bg-transparent px-0 py-3 font-body text-base text-[var(--fama-ink)] focus:ring-0"
                                        >
                                            <SelectValue placeholder="Seleziona..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {PROPERTY_TYPES.map((p) => (
                                                <SelectItem
                                                    key={p}
                                                    value={p}
                                                    data-testid={`property-option-${p}`}
                                                >
                                                    {p}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-[var(--fama-ink-soft)]">
                                        Messaggio
                                    </label>
                                    <textarea
                                        value={form.message}
                                        onChange={(e) => update("message", e.target.value)}
                                        rows={3}
                                        placeholder="Raccontaci del tuo immobile..."
                                        className={`${inputCls} resize-none`}
                                        data-testid="contact-input-message"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="contact-submit"
                                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--fama-gold)] px-8 py-4 font-body text-base font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#b28e1f] disabled:cursor-not-allowed disabled:opacity-70"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 size={18} className="animate-spin" />
                                            Invio in corso...
                                        </>
                                    ) : (
                                        "Richiedi una valutazione gratuita"
                                    )}
                                </button>
                                <p className="text-center font-body text-xs text-[var(--fama-ink-soft)]">
                                    Inviando accetti la nostra Privacy Policy.
                                </p>
                            </form>
                        )}
                    </div>
                </FadeUp>
            </div>
        </section>
    );
}
